import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, StyleSheet, View } from 'react-native';
import { SECOND } from 'time-constants';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct, useWeatherAlertsHeadlines } from '@hooks';

import { TWCAnimatedAlertIcon } from '../../primitives/TWCAnimatedAlertIcon/TWCAnimatedAlertIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { LDL } from '../styles';

const tPath = 'lowerDisplayLine.alertHeadlines';
const testIDPath = lowerDisplayLineTestIDs.alertHeadlines;
const displayDurationPerPage = 5 * SECOND;

/**
 * LDL Alert Headlines
 * The Alert Headline feature gives users information about long-lasting weather alerts.
 * These headlines are usually paired with longer crawls and are only shown when the LDL is not operating in crawl mode.
 * Each page will display for a duration of 5 seconds
 */
const LDLAlertHeadlinesAPI = () => {
  const data = useWeatherAlertsHeadlines();
  const showNextProduct = useShowNextLDLProduct();
  const hasAlerts = data.length > 0;

  useEffect(() => {
    !hasAlerts && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAlerts]);

  return hasAlerts ? <LDLAlertHeadlinesUI data={data} onFinish={showNextProduct} /> : <></>;
};

const LDLAlertHeadlinesUI = ({ data, onFinish }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });

  const pageSlideAnimation = useRef(new Animated.Value(0)).current;

  const animations = data.map((_, index) =>
    Animated.timing(pageSlideAnimation, {
      delay: displayDurationPerPage,
      toValue: -LDL.height * (data.length - 1 > index ? index + 1 : index),
      duration: 500,
      useNativeDriver: true,
    }),
  );

  useEffect(() => {
    const animation = Animated.sequence(animations);

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TWCText style={styles.productText} testID={testIDPath.productLabel} variant={'condensedText36Transparent'}>
        {t('productLabel', { count: data.length })}
      </TWCText>
      <View style={styles.alertsContainer}>
        <Animated.View style={[styles.pageWrapper, { transform: [{ translateY: pageSlideAnimation }] }]}>
          {data.map(({ detailKey, eventDescription, headlineText }, index) => (
            <View key={detailKey} style={styles.alertItemWrapper}>
              <TWCAnimatedAlertIcon eventDescription={eventDescription} />
              <TWCText
                numberOfLines={1}
                style={styles.itemText}
                testID={testIDPath[`alert${index + 1}`]}
                variant={'condensedText36'}
              >
                {`${headlineText}`}
              </TWCText>
            </View>
          ))}
        </Animated.View>
      </View>
    </>
  );
};

export const LDLAlertHeadlines = {
  API: LDLAlertHeadlinesAPI,
  UI: LDLAlertHeadlinesUI,
};

const styles = StyleSheet.create({
  alertItemWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingVertical: Theme.px(6),
  },
  alertsContainer: {
    flexGrow: 1,
    height: LDL.height,
  },
  itemText: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingLeft: Theme.px(18),
  },
  pageWrapper: {
    position: 'absolute',
    width: '100%',
  },
  productText: {
    paddingRight: Theme.px(48),
  },
});
